import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChessAppLayout } from "@/components/ChessAppLayout";
import { AvatarDisplay } from "@/components/AvatarDisplay";
import { PlayerStats } from "@/components/PlayerStats";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { BadgeDisplay } from "@/components/BadgeDisplay";
import { GameHistorySection } from "@/components/GameHistorySection";
import { PlayerFriendsSection } from "@/components/PlayerFriendsSection";
import { PlayerStatisticsPanel } from "@/components/PlayerStatisticsPanel";
import { PlayerComparisonPanel } from "@/components/PlayerComparisonPanel";
import { AchievementsPanel } from "@/components/AchievementsPanel";
import { useSEO } from "@/hooks/useSEO";
import { useAuth } from "@/hooks/useAuth";
import { usePrivateRooms } from "@/hooks/usePrivateRooms";
import { useFakeFriendships } from "@/hooks/useFakeFriendships";
import { ChallengeDialog } from "@/components/ChallengeDialog";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, Trophy, UserX, UserPlus, Users, Zap, Lock, Heart, HeartOff } from "lucide-react";
import type { Database } from "@/integrations/supabase/types";
import { generateFakeLeaderboard, FakePlayer } from "@/lib/fake-leaderboard-generator";

type UserProfile = Database['public']['Tables']['user_profiles']['Row'];

export default function PlayerProfile() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { friends, sendFriendRequest, invitations } = usePrivateRooms();
  const { isFakeFriend, toggleFakeFriend } = useFakeFriendships();
  
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [fakePlayer, setFakePlayer] = useState<FakePlayer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isPrivateProfile, setIsPrivateProfile] = useState(false);
  const [showChallengeDialog, setShowChallengeDialog] = useState(false);
  
  const isFakePlayer = userId?.startsWith('fake-player-') ?? false;
  
  // Check friendship status (only for real players)
  const isFriend = !isFakePlayer && friends.some(
    f => (f.requester_id === userId || f.addressee_id === userId) && f.status === 'accepted'
  );
  
  // Check if there's a pending friend request (only for real players)
  const hasPendingRequest = !isFakePlayer && friends.some(
    f => f.addressee_id === userId && f.status === 'pending'
  );
  
  // Check fake friendship
  const isFakeFriendStatus = isFakePlayer && userId ? isFakeFriend(userId) : false;
  
  const isOwnProfile = user?.id === userId;

  useEffect(() => {
    const fetchProfile = async () => {
      if (!userId) return;

      setLoading(true);
      setError(false);
      setIsPrivateProfile(false);
      setFakePlayer(null);
      setProfile(null);

      // Handle fake player IDs
      if (userId.startsWith('fake-player-')) {
        const fakePlayers = generateFakeLeaderboard();
        const foundFakePlayer = fakePlayers.find(p => p.id === userId);
        
        if (foundFakePlayer) {
          if (foundFakePlayer.isPrivate) {
            setIsPrivateProfile(true);
          } else {
            setFakePlayer(foundFakePlayer);
          }
        } else {
          setError(true);
        }
        setLoading(false);
        return;
      }

      // Regular database lookup for real players
      try {
        const { data, error: fetchError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', userId)
          .single();

        if (fetchError) {
          console.error('Error fetching profile:', fetchError);
          setError(true);
          return;
        }

        setProfile(data);
      } catch (err) {
        console.error('Error:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [userId]);

  const handleSendFriendRequest = async () => {
    if (!userId) return;
    
    try {
      await sendFriendRequest(userId);
      toast({
        title: "Friend Request Sent",
        description: `Friend request sent to ${profile?.username || profile?.display_name}`,
      });
    } catch (error) {
      console.error('Error sending friend request:', error);
      toast({
        title: "Error",
        description: "Could not send friend request",
        variant: "destructive",
      });
    }
  };

  const handleFakeFriendToggle = () => {
    if (!userId || !fakePlayer) return;
    
    const added = toggleFakeFriend(userId, fakePlayer.username);
    toast({
      title: added ? "Friend Added!" : "Friend Removed",
      description: added 
        ? `${fakePlayer.username} is now your friend!` 
        : `${fakePlayer.username} removed from friends`,
    });
  };

  const handleChallengeClick = () => {
    if (isFakePlayer && fakePlayer) {
      // Navigate to home with bot match params
      navigate(`/?botMatch=${encodeURIComponent(fakePlayer.username)}&botElo=${fakePlayer.elo_rating}`);
    } else {
      setShowChallengeDialog(true);
    }
  };

  // Create display profile from either real profile or fake player
  const displayProfile = fakePlayer ? {
    id: fakePlayer.id,
    username: fakePlayer.username,
    display_name: fakePlayer.display_name,
    elo_rating: fakePlayer.elo_rating,
    games_played: fakePlayer.games_played,
    games_won: fakePlayer.games_won,
    games_lost: fakePlayer.games_lost,
    games_drawn: fakePlayer.games_drawn,
    avatar_config: fakePlayer.avatarConfig,
    avatar_url: null,
  } : profile;

  useSEO({
    title: displayProfile 
      ? `${displayProfile.username || displayProfile.display_name || 'Player'} - Chess Profile | ChessHatch`
      : "Player Profile | ChessHatch",
    description: displayProfile 
      ? `View ${displayProfile.username || displayProfile.display_name}'s chess profile. ELO: ${displayProfile.elo_rating}. ${displayProfile.games_played} games played.`
      : "View player chess profile and statistics on ChessHatch",
    keywords: "chess profile, chess player stats, chess ELO"
  });

  if (loading) {
    return (
      <ChessAppLayout>
        <div className="container max-w-4xl mx-auto py-8 px-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/players')}
            className="mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Search
          </Button>

          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                <Skeleton className="w-24 h-24 rounded-full" />
                <div className="flex-1 text-center md:text-left">
                  <Skeleton className="h-8 w-48 mb-2 mx-auto md:mx-0" />
                  <Skeleton className="h-6 w-32 mb-4 mx-auto md:mx-0" />
                  <Skeleton className="h-10 w-24 mx-auto md:mx-0" />
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-2">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <Skeleton className="h-20 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </ChessAppLayout>
    );
  }

  // Private profile message for private fake players
  if (isPrivateProfile) {
    return (
      <ChessAppLayout>
        <div className="container max-w-4xl mx-auto py-8 px-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/players')}
            className="mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Search
          </Button>

          <Card className="mb-6">
            <CardContent className="py-12 text-center">
              <Lock className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-2xl font-bold mb-2">Private Profile</h2>
              <p className="text-muted-foreground mb-6">
                This player has set their profile to private
              </p>
              <Button onClick={() => navigate('/players')}>
                Back to Player Search
              </Button>
            </CardContent>
          </Card>
        </div>
      </ChessAppLayout>
    );
  }

  // Error state - player not found
  if (error || !displayProfile) {
    return (
      <ChessAppLayout>
        <div className="container max-w-4xl mx-auto py-8 px-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/players')}
            className="mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Search
          </Button>

          <Card className="mb-6">
            <CardContent className="py-12 text-center">
              <UserX className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-2xl font-bold mb-2">Player Not Found</h2>
              <p className="text-muted-foreground mb-6">
                This player doesn't exist or their profile is unavailable
              </p>
              <Button onClick={() => navigate('/players')}>
                Back to Player Search
              </Button>
            </CardContent>
          </Card>
        </div>
      </ChessAppLayout>
    );
  }

  return (
    <ChessAppLayout>
      <div className="container max-w-4xl mx-auto py-8 px-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/players')}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Search
        </Button>

        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
              <AvatarDisplay
                username={displayProfile.username || displayProfile.display_name}
                avatarConfig={displayProfile.avatar_config as any}
                avatarUrl={displayProfile.avatar_url}
                size="xl"
              />
              <div className="flex-1 text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                  <h1 className="text-3xl font-bold">
                    {displayProfile.username || displayProfile.display_name || 'Anonymous Player'}
                  </h1>
                  {isFakePlayer && isFakeFriendStatus && (
                    <Badge variant="outline" className="text-teen-pink border-teen-pink/30">
                      <Heart className="w-3 h-3 mr-1 fill-teen-pink" />
                      Friend
                    </Badge>
                  )}
                </div>
                {displayProfile.display_name && displayProfile.username !== displayProfile.display_name && (
                  <p className="text-muted-foreground mb-3">"{displayProfile.display_name}"</p>
                )}
                <Badge variant="default" className="gap-2 text-lg px-4 py-1">
                  <Trophy className="w-5 h-5" />
                  {displayProfile.elo_rating} ELO
                </Badge>
                
              </div>
            </div>

            {/* Action Buttons - Only show if not own profile and user is logged in */}
            {!isOwnProfile && user && (
              <div className="flex flex-wrap gap-2 mt-6 pt-6 border-t">
                {/* Add Friend button - for fake players (localStorage) */}
                {isFakePlayer && (
                  <Button 
                    onClick={handleFakeFriendToggle}
                    variant={isFakeFriendStatus ? "outline" : "default"}
                  >
                    {isFakeFriendStatus ? (
                      <>
                        <HeartOff className="w-4 h-4 mr-2" />
                        Remove Friend
                      </>
                    ) : (
                      <>
                        <Heart className="w-4 h-4 mr-2" />
                        Add Friend
                      </>
                    )}
                  </Button>
                )}
                
                {/* Add Friend button - for real players */}
                {!isFakePlayer && !isFriend && !hasPendingRequest && (
                  <Button onClick={handleSendFriendRequest}>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Friend
                  </Button>
                )}
                
                {!isFakePlayer && hasPendingRequest && (
                  <Badge variant="secondary" className="py-2 px-4">
                    Friend Request Sent
                  </Badge>
                )}
                
                {!isFakePlayer && isFriend && (
                  <Badge variant="default" className="gap-2 py-2 px-4">
                    <Users className="w-4 h-4" />
                    Friends
                  </Badge>
                )}
                
                <Button 
                  variant={isFriend || isFakeFriendStatus ? "default" : "outline"}
                  onClick={handleChallengeClick}
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Challenge
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <PlayerStats
          eloRating={displayProfile.elo_rating}
          gamesPlayed={displayProfile.games_played}
          gamesWon={displayProfile.games_won}
          gamesLost={displayProfile.games_lost}
          gamesDrawn={displayProfile.games_drawn}
        />

        {/* Only show database-dependent sections for real players */}
        {!isFakePlayer && profile && (
          <>
            {/* Detailed Statistics Panel */}
            <PlayerStatisticsPanel
              userId={profile.id}
              currentElo={profile.elo_rating}
              gamesPlayed={profile.games_played}
              gamesWon={profile.games_won}
              gamesLost={profile.games_lost}
              gamesDrawn={profile.games_drawn}
            />

            {/* Player Comparison Panel */}
            <PlayerComparisonPanel
              userId={profile.id}
              currentElo={profile.elo_rating}
              gamesPlayed={profile.games_played}
              gamesWon={profile.games_won}
              gamesLost={profile.games_lost}
              gamesDrawn={profile.games_drawn}
            />

            {/* Achievements & Milestones Section */}
            <AchievementsPanel />

            {/* Legacy Badges Section */}
            <Card>
              <CardHeader>
                <CardTitle>Legacy Badges</CardTitle>
                <CardDescription>Classic badges and accomplishments</CardDescription>
              </CardHeader>
              <CardContent>
                <BadgeDisplay 
                  userId={profile.id} 
                  layout="grid" 
                  maxDisplay={12}
                  showDetails={true}
                />
              </CardContent>
            </Card>

            {/* Game History Section */}
            <GameHistorySection userId={profile.id} maxGames={10} />

            {/* Friends Section */}
            <PlayerFriendsSection userId={profile.id} maxDisplay={12} />
          </>
        )}
      </div>

      {/* Challenge Dialog - only for real players */}
      {!isFakePlayer && profile && (
        <ChallengeDialog
          targetUserId={profile.id}
          targetUsername={profile.username || profile.display_name || 'Player'}
          targetElo={profile.elo_rating}
          isFriend={isFriend}
          open={showChallengeDialog}
          onOpenChange={setShowChallengeDialog}
        />
      )}
    </ChessAppLayout>
  );
}
